import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { UserDropdown } from "./UserDropdown";
import { useAuth } from "../hooks/useAuth";
import { agentKeys } from "../queryKeys";

function getPathname() {
  return typeof window !== "undefined" ? window.location.pathname : "/";
}

interface Agent {
  id: string;
  name: string;
  display_name: string;
  status: string;
}

interface AgentEditorLayoutProps {
  children: React.ReactNode;
  agentId: string;
  onSave?: () => void;
}

export function AgentEditorLayout({ 
  children, 
  agentId,
  onSave 
}: AgentEditorLayoutProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = getPathname();

  const { data: agentData } = useQuery({
    queryKey: agentKeys.detail(agentId),
    queryFn: () => apiFetch<{ agent: Agent }>(`/api/agents/${agentId}`).then((r) => r.agent),
  });
  const agent = agentData ?? null;

  function handleSettingsClick() {
    window.location.href = "/settings";
  }

  async function handleSave() {
    setSaving(true);
    setSaveSuccess(false);
    try {
      const saveHandler = (window as any).__agentSaveHandler || 
                         (window as any).__personalitySaveHandler ||
                         (window as any).__integrationsSaveHandler;
      if (saveHandler) {
        await saveHandler();
        setSaveSuccess(true);
        queryClient.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
        setTimeout(() => setSaveSuccess(false), 2000);
      } else {
        console.warn("No save handler found");
      }
      
      if (onSave) {
        await onSave();
      }
    } catch (err) {
      console.error("Save failed:", err);
      setSaveSuccess(false);
    } finally {
      setSaving(false);
    }
  }

  const activeTab = pathname.includes("/general") ? "general" :
                    pathname.includes("/knowledge") ? "knowledge" :
                    pathname.includes("/personality") ? "personality" :
                    pathname.includes("/integrations") ? "integrations" :
                    pathname.includes("/chats") ? "chats" :
                    pathname.includes("/analytics") ? "analytics" :
                    pathname.includes("/danger") ? "danger" : "general";

  const displayName = agent?.display_name || agent?.name || "Agent";
  const userName = user?.display_name || user?.email?.split("@")[0] || "User";
  const plan = user?.plan || "free";

  const navItems = [
    { id: "general", label: "General", icon: "settings" },
    { id: "personality", label: "Personality", icon: "psychology" },
    { id: "knowledge", label: "Knowledge", icon: "menu_book" },
    { id: "integrations", label: "Integrations", icon: "extension" },
    { id: "chats", label: "Chats", icon: "chat" },
    { id: "analytics", label: "Analytics", icon: "analytics" },
    { id: "danger", label: "Danger zone", icon: "warning" },
  ];

  return (
    <div className={`agent-editor-layout ${menuOpen ? "mobile-menu-open" : ""}`}>
      {/* Mobile: backdrop to close menu */}
      <div
        className="sidebar-backdrop"
        aria-hidden="true"
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile top bar: hamburger + title + Save */}
      <header className="mobile-top-bar">
        <button
          type="button"
          className="mobile-menu-btn"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
        >
          <span className="material-symbols-outlined">menu</span>
        </button>
        <span className="mobile-top-bar-title">Agent Builder</span>
        <button
          className={`btn-publish mobile-save-btn ${saveSuccess ? "btn-saved" : ""}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "…" : saveSuccess ? "Saved!" : "Save"}
        </button>
      </header>

      <aside className="agent-editor-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-header-row">
            <div className="sidebar-logo-section">
              <div className="sidebar-logo-icon">
                <span className="material-symbols-outlined">smart_toy</span>
              </div>
              <h1 className="sidebar-logo-title">Agent Builder</h1>
            </div>
            <button
              type="button"
              className="sidebar-close-btn"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <p className="sidebar-logo-subtitle">AI Chat Management</p>
        </div>

        <nav className="agent-editor-nav">
          {navItems.map(item => (
            <a
              key={item.id}
              href={`/agents/${agentId}/${item.id}`}
              className={`editor-nav-item ${activeTab === item.id ? "active" : ""}`}
              onClick={() => setMenuOpen(false)}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              <span>{item.label}</span>
            </a>
          ))}
        </nav>

        <div className="sidebar-footer-section">
          <UserDropdown
            userName={userName}
            userPlan={plan}
            onSettingsClick={() => { setMenuOpen(false); handleSettingsClick(); }}
          />
        </div>
      </aside>

      <main className="agent-editor-main">
        <header className="agent-editor-header">
          <div className="header-left">
            <div className="header-breadcrumb">
              <a href="/dashboard">Agents</a>
              <span className="material-symbols-outlined">chevron_right</span>
              <span className="breadcrumb-current">{agent?.name || "Agent"}</span>
            </div>
            <h2 className="header-title">Agent: {displayName}</h2>
          </div>
          <div className="header-actions header-actions-desktop">
            <button
              className={`btn-publish ${saveSuccess ? "btn-saved" : ""}`}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : saveSuccess ? "Saved!" : "Save"}
            </button>
          </div>
        </header>

        <div className="agent-editor-content">
          {children}
        </div>
      </main>
    </div>
  );
}
